import React, { useState, useCallback, useReducer, useRef } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Form, Input, Button, message } from 'antd';
import * as HttpStatus from 'http-status-codes';
import PhoneInput, { isPossiblePhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { updateUser, sendMobileOtp, verifyMobileOtp, disableAuthenticator } from '../UserActions';
import { userInfoSelector } from '../UserSelectors';
import { sms2faUserSettingEnabledSelector } from '../../../../Organisation/OrganisationSelectors';
import { MSG_ERROR } from '../../../../../constants';
import { getApiErrorMessage } from '../../../../../utils/httpError';
import {
  OTP_TYPE,
  MFA_COPY,
  isSmsSecondFactorOptionAvailable,
  isAuthenticatorEnrollmentPending,
  isAuthenticatorOtpType,
  getAuthenticatorAppLabel,
  getOtpTypeLabel,
} from '../../../../../constants/mfa';
import { trimFormFieldValues } from './user-edit-form-utils';
import { UserEditPhoneVerification, phoneOtpStatusMessage } from './UserEditPhoneVerification';
import { UserEditSecondFactorSection } from './UserEditSecondFactorSection';
import AuthenticatorEnrollmentModal from './AuthenticatorEnrollmentModal';
import { RemoveAuthenticatorModal } from './RemoveAuthenticatorModal';
import './UserEdit.scss';
import 'react-phone-number-input/style.css';

const FormItem = Form.Item;

const partnerAllowsSms = isSmsSecondFactorOptionAvailable;

function readPhoneText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/** National input ("430477676" + AU) and E.164 ("+61430477676") both become +E.164. */
function toE164Phone(value, country) {
  const text = readPhoneText(value);
  if (!text) return null;

  const parsed = text.startsWith('+') ? parsePhoneNumber(text) : parsePhoneNumber(text, country);
  if (!parsed || !isPossiblePhoneNumber(parsed.number)) return null;
  return parsed.number;
}

function UserEdit({
  form,
  userInfo = {},
  userRequest,
  updateUser: patchUser,
  sendMobileOtp: dispatchSendMobileOtp,
  verifyMobileOtp: dispatchVerifyMobileOtp,
  disableAuthenticator: dispatchDisableAuthenticator,
  close,
  sms2faUserSettingEnabled,
}) {
  const { getFieldDecorator } = form;
  const [requireMobileVerification, setRequireMobileVerification] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(userInfo.phoneNumber);
  const [phoneCountry, setPhoneCountry] = useState(sms2faUserSettingEnabled ? 'AU' : 'GB');
  const [codeSent, setCodeSent] = useState(false);
  const [otpCode, setOtpCode] = useState();
  const [otpStatus, setOtpStatus] = useState(phoneOtpStatusMessage.required);
  const [enrollmentModalVisible, setEnrollmentModalVisible] = useState(false);
  const [removeAuthenticatorOpen, setRemoveAuthenticatorOpen] = useState(false);
  const [, bumpFormUi] = useReducer((n) => n + 1, 0);
  const [pendingOtpType, setPendingOtpType] = useState(null);
  const removeConfirmedRef = useRef(false);

  const codeInputRef = useCallback((inputElement) => {
    if (inputElement) inputElement.focus();
  }, []);

  const requestAuthenticatorRemoval = (nextOtpType) => {
    setPendingOtpType(nextOtpType ?? OTP_TYPE.EMAIL);
    setRemoveAuthenticatorOpen(true);
  };

  const closeAuthenticatorRemoval = () => {
    setRemoveAuthenticatorOpen(false);
    if (!removeConfirmedRef.current && isAuthenticatorOtpType(userInfo.otpType)) {
      form.setFieldsValue({ otpType: userInfo.otpType });
      bumpFormUi();
    }
    removeConfirmedRef.current = false;
    setPendingOtpType(null);
  };

  const confirmAuthenticatorRemoval = () => {
    removeConfirmedRef.current = true;
    form.setFieldsValue({ otpType: OTP_TYPE.EMAIL });
    bumpFormUi();
  };

  const onPhoneNumberChange = (value) => {
    setPhoneNumber(value);
    if (!partnerAllowsSms()) {
      setRequireMobileVerification(false);
      return;
    }
    if (value !== userInfo.phoneNumber) {
      setRequireMobileVerification(true);
      setOtpStatus(phoneOtpStatusMessage.required);
      setCodeSent(false);
    } else {
      setRequireMobileVerification(false);
    }
  };

  const onOtpCodeChange = (e) => {
    setOtpCode(e.target.value);
  };

  const sendPhoneOtp = async () => {
    try {
      const rawPhone = readPhoneText(phoneNumber)
        || readPhoneText(form.getFieldValue('phoneNumber'))
        || readPhoneText(userInfo.phoneNumber);
      const currentPhone = toE164Phone(rawPhone, phoneCountry);
      if (!currentPhone) {
        setOtpStatus(phoneOtpStatusMessage.errorWrongMobile);
        return;
      }

      setPhoneNumber(currentPhone);
      form.setFieldsValue({ phoneNumber: currentPhone });
      await dispatchSendMobileOtp({ userId: userInfo.id, action: 1, phoneNumber: currentPhone });
      setOtpStatus(phoneOtpStatusMessage.sent);
      setCodeSent(true);
    } catch (err) {
      setOtpStatus({ type: 'error', message: err });
    }
  };

  const verifyPhoneOtp = async () => {
    try {
      await dispatchVerifyMobileOtp({ userId: userInfo.id, action: 1, code: `${otpCode}` });
      setOtpStatus(phoneOtpStatusMessage.verified);
      setRequireMobileVerification(false);
    } catch (err) {
      setOtpStatus({ type: 'error', message: err });
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    form.validateFields(async (err, fieldsValue) => {
      if (err || requireMobileVerification) return;

      const values = trimFormFieldValues(fieldsValue);
      const targetOtp = values.otpType;

      if (isAuthenticatorEnrollmentPending(targetOtp, userInfo.otpType)) {
        message.error(MFA_COPY.profileSaveNeedsAuthenticatorEnrollment);
        return;
      }

      if (isAuthenticatorOtpType(userInfo.otpType) && !isAuthenticatorOtpType(targetOtp)) {
        message.error(MFA_COPY.profileSaveCannotDropAuthenticatorWithoutRemove);
        return;
      }

      try {
        await patchUser(values);
        close();
        if (values.email !== userInfo.email) {
          message.success(
            'You requested changing your email address. A confirmation message has been sent to your new address — check your inbox to complete the update.',
            10,
          );
        } else {
          message.success('Your profile has been updated.');
        }
      } catch (submitErr) {
        if (submitErr && submitErr.status === HttpStatus.CONFLICT) {
          message.error('This email address is already in use.');
        } else {
          message.error(getApiErrorMessage(submitErr, MSG_ERROR));
        }
      }
    });
  };

  const formOtpType = form.getFieldValue('otpType');
  const mustRemoveAuthenticator = isAuthenticatorOtpType(userInfo.otpType) && formOtpType !== userInfo.otpType;
  const saveDisabledForMfa =
    requireMobileVerification
    || isAuthenticatorEnrollmentPending(formOtpType, userInfo.otpType)
    || mustRemoveAuthenticator;

  return (
    <Form className="user-edit-form">
      <Row gutter={20} className="user-edit-name-row">
        <Col span={12}>
          <FormItem label="First name">
            {getFieldDecorator('firstName', { initialValue: userInfo.firstName })(
              <Input placeholder="First name" disabled={userRequest} spellCheck={false} />,
            )}
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem label="Last name">
            {getFieldDecorator('lastName', { initialValue: userInfo.lastName })(
              <Input placeholder="Last name" disabled={userRequest} spellCheck={false} />,
            )}
          </FormItem>
        </Col>
      </Row>

      <FormItem label="Email">
        {getFieldDecorator('email', {
          initialValue: userInfo.email,
          validateTrigger: 'onBlur',
          rules: [{ type: 'email', message: 'Please enter a valid email address.' }],
        })(<Input placeholder="Email" disabled={userRequest} spellCheck={false} />)}
      </FormItem>

      {partnerAllowsSms() && (
        <FormItem label="Phone number">
          {getFieldDecorator('phoneNumber', {
            initialValue: userInfo?.phoneNumber,
            validateTrigger: 'onBlur',
          })(
            <PhoneInput
              countrySelectProps={{ unicodeFlags: true }}
              countryCallingCodeEditable={false}
            defaultCountry={sms2faUserSettingEnabled ? 'AU' : 'GB'}
            disabled={userRequest}
            onCountryChange={setPhoneCountry}
            onChange={onPhoneNumberChange}
            />,
          )}
        </FormItem>
      )}

      <UserEditPhoneVerification
        requireMobileVerification={requireMobileVerification && partnerAllowsSms()}
        otpStatus={otpStatus}
        codeSent={codeSent}
        userRequest={userRequest}
        codeInputRef={codeInputRef}
        onOtpCodeChange={onOtpCodeChange}
        onSendOtp={sendPhoneOtp}
        onVerifyOtp={verifyPhoneOtp}
        getFieldDecorator={getFieldDecorator}
      />

      <UserEditSecondFactorSection
        form={form}
        userInfo={userInfo}
        userRequest={userRequest}
        phoneNumber={phoneNumber}
        requireMobileVerification={requireMobileVerification}
        onOtpTypeChange={bumpFormUi}
        onOpenEnrollment={() => setEnrollmentModalVisible(true)}
        onConfirmRemoveAuthenticator={requestAuthenticatorRemoval}
        getFieldDecorator={getFieldDecorator}
      />

      <AuthenticatorEnrollmentModal
        visible={enrollmentModalVisible}
        otpType={isAuthenticatorOtpType(formOtpType) ? formOtpType : OTP_TYPE.AUTHENTICATOR}
        onCancel={() => setEnrollmentModalVisible(false)}
        onCompleted={() => {
          if (isAuthenticatorOtpType(formOtpType)) form.setFieldsValue({ otpType: formOtpType });
        }}
      />

      <RemoveAuthenticatorModal
        visible={removeAuthenticatorOpen}
        userRequest={userRequest}
        appLabel={getAuthenticatorAppLabel(userInfo.otpType)}
        nextLabel={getOtpTypeLabel(pendingOtpType ?? OTP_TYPE.EMAIL)}
        nextIsAuthenticator={isAuthenticatorOtpType(pendingOtpType)}
        onClose={closeAuthenticatorRemoval}
        onSuccess={confirmAuthenticatorRemoval}
        disableAuthenticator={dispatchDisableAuthenticator}
      />

      <FormItem className="user-edit-save-footer">
        <Button type="primary" disabled={saveDisabledForMfa} block loading={userRequest} onClick={onSubmit}>
          Save
        </Button>
      </FormItem>
    </Form>
  );
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  userRequest: state.userReducer.loadingUser,
  sms2faUserSettingEnabled: sms2faUserSettingEnabledSelector(state),
});

const mapDispatchToProps = {
  updateUser,
  sendMobileOtp,
  verifyMobileOtp,
  disableAuthenticator,
};

export default connect(mapStateToProps, mapDispatchToProps)(Form.create()(UserEdit));
